import { Injectable } from '@angular/core';
import { Location } from '@interfaces/rest/interfaces/journey';
import { Coordinates } from '@interfaces/rest/interfaces/zone';
import { RestService } from './rest.service';

export interface Destination {
  main: string;
  secondary: string;
  placeId: string;
}

export interface DestinationDetail {
  lat: number;
  lng: number;
  main: string;
  secondary: string;
}

interface AddressZoneCache {
  name: string;
  coordinates: { lat: number; lng: number }[];
  addressLat: number;
  addressLng: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private addressZones: AddressZoneCache[] = [];
  private addressZonesLoaded = false;

  constructor(private rest: RestService) {
    this.loadAddressZones();
  }

  async loadAddressZones(): Promise<void> {
    try {
      const resp = await this.rest.getGlobalZones();
      if (resp.ok && resp.response) {
        this.addressZones = resp.response
          .filter((z: any) => z.type === 'address' && z.addressLat != null && z.addressLng != null && z.coordinates?.length)
          .map((z: any) => ({
            name: z.name,
            coordinates: z.coordinates,
            addressLat: z.addressLat,
            addressLng: z.addressLng
          }));
      }
      this.addressZonesLoaded = true;
    } catch (error) {
      console.error('Error loading address zones:', error);
    }
  }

  private searchAddressZones(text: string): Location[] {
    const lower = text.toLowerCase();
    return this.addressZones
      .filter(z => z.name.toLowerCase().includes(lower))
      .map(z => ({
        lat: z.addressLat,
        lng: z.addressLng,
        main: z.name,
        secondary: 'Mallorca'
      }));
  }

  async geolocationGoogle(searchValue: string): Promise<Location[]> {
    let locations: Location[] = [];
    if (!searchValue) {
      return locations;
    }
    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(39.21336405907106, 2.286124031276248),
      new google.maps.LatLng(39.996529359153115, 3.529999646106621)
    );
    const geocoder = new google.maps.Geocoder();
    const results = await geocoder.geocode({ address: searchValue, bounds, language: 'es' });
    console.log('results geocoder', results);
    results.results.forEach(elem => {
      console.log('elem lat', elem.geometry.location.lat());
      console.log('elem lng', elem.geometry.location.lng());
      const address = this.formatAddress(elem.formatted_address);
      locations.push({
        lat: elem.geometry.location.lat(),
        lng: elem.geometry.location.lng(),
        main: address.title,
        secondary: address.description
      });
    });

    return locations;
  }

  async geolocation(searchValue: string): Promise<Location[]> {
    try {
      if (!searchValue) {
        return [];
      }

      const localMatches = this.searchAddressZones(searchValue);

      const bias = '3.0555253593562384,39.75359497208257,3.1706673032424533,39.91549786704198';
      const resultSuggestions = await this.rest.getSuggestionsGeoapify(searchValue, bias);
      console.log('resultSuggestions', resultSuggestions);

      const locations = [...localMatches, ...(resultSuggestions || [])];

      if (!locations.length) {
        const resultGoogle = await this.geolocationGoogle(searchValue);
        console.log('resultGoogle', resultGoogle);
        if (resultGoogle.length) {
          return resultGoogle;
        }
      }
      return locations;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async getAddressByPlaceId(placeId: string): Promise<Coordinates | null> {
    return new Promise(async resolve => {

      const geocoder = new google.maps.Geocoder();
      try {
        await geocoder.geocode({ placeId }, (result: any, status: any) => {
          console.log('result', result);
          console.log('status', status);

          if (result?.[0]) {
            const coordinates: Coordinates = {
              lat: result?.[0].geometry.location.lat(),
              lng: result?.[0].geometry.location.lng()
            }
            resolve(coordinates);
          } else {
            resolve(null);
          }
        });
      } catch (error) {
        console.log(error);
      }
    });
  }

  async getAddressByCoords(lat: number, lng: number): Promise<any> {
    const geocoder = new google.maps.Geocoder();

    return new Promise<void>(async (resolve, reject) => {
      await geocoder.geocode({ location: { lat, lng } }, (result: any, status: any) => {
        console.log('result', result);
        console.log('status', status);

        if (result?.[0]) {
          resolve(result?.[0]);
        } else {
          reject();
        }
      });
    })

  }

  formatAddress(address: string) {
    const addressParts = address.split(', ');

    let title, description;

    if (addressParts.length > 2) {
      title = addressParts.slice(0, 2).join(', ');
      description = addressParts.slice(2).join(', ');
    } else {
      title = addressParts[0] || 'Desconocido';
      description = addressParts.slice(1).join(', ') || 'Desconocido';
    }

    return { title, description };
  }

  // getAddressByInput() {
  //   // 1. DEFINIR LOS LÍMITES GEOGRÁFICOS
  //   const bounds = new google.maps.LatLngBounds(
  //     new google.maps.LatLng(39.21336405907106, 2.286124031276248),
  //     new google.maps.LatLng(39.996529359153115, 3.529999646106621)
  //   );

  //   const input = document.getElementById('address-input');

  //   // 2. CREAR EL OBJETO AUTOCOMPLETE CON LAS OPTIMIZACIONES
  //   const autocomplete = new google.maps.places.Autocomplete(input, {
  //     bounds: bounds,         // Restringe la búsqueda a esta zona
  //     strictBounds: true,     // ¡Importante! No muestra resultados fuera de los límites
  //     componentRestrictions: { 'country': 'es' }, // Filtra por país (España)

  //     // ¡CLAVE PARA AHORRAR! Pide solo los campos que usarás después en Place Details.
  //     // Al pedir estos aquí, la llamada a getPlace() que hace el widget es más barata.
  //     fields: ['address_components', 'geometry', 'name', 'formatted_address']
  //   });

  //   // 3. GESTIONAR LA SELECCIÓN DEL USUARIO
  //   autocomplete.addListener('place_changed', () => {
  //     const place = autocomplete.getPlace();
  //     const resultOutput = document.getElementById('result');

  //     // Comprobar que se ha seleccionado un lugar válido
  //     if (!place.geometry || !place.geometry.location) {
  //       resultOutput.textContent = "No se encontraron detalles para esta dirección.";
  //       return;
  //     }

  //     // Extraer la información necesaria del objeto 'place'
  //     const address = place.formatted_address;
  //     const street = place.name; // O puedes iterar en address_components para más detalle
  //     const city = place.address_components.find(c => c.types.includes('locality'))?.long_name || '';
  //     const lat = place.geometry.location.lat();
  //     const lng = place.geometry.location.lng();

  //     // Mostrar los resultados
  //     const resultText = `
  //     Dirección Completa: ${address}
  //     Calle/Nombre: ${street}
  //     Ciudad: ${city}
  //     Latitud: ${lat}
  //     Longitud: ${lng}
  //   `;
  //     resultOutput.textContent = resultText;

  //     // Aquí ya tendrías los datos para enviar a tu backend o usar en la app
  //     console.log({
  //       address,
  //       street,
  //       city,
  //       coordinates: { lat, lng }
  //     });
  //   });
  //   }

  /**
   * Inicializa Google Autocomplete (NUEVO - PlaceAutocompleteElement) en un contenedor HTML
   * 
   * IMPORTANTE: Este método usa el nuevo google.maps.places.PlaceAutocompleteElement
   * recomendado por Google desde marzo 2025.
   * 
   * EJEMPLO DE USO:
   * ```typescript
   * // En el componente:
   * @ViewChild('addressContainer') addressContainer!: ElementRef<HTMLDivElement>;
   * private autocompleteInstance: any;
   * 
   * ngAfterViewInit() {
   *   this.autocompleteInstance = this.locationService.initializeGoogleAutocomplete(
   *     this.addressContainer.nativeElement,
   *     (placeData) => {
   *       // Manejar la selección del lugar
   *       this.selectedAddress = placeData.formatted_address;
   *       this.coordinates = { lat: placeData.lat, lng: placeData.lng };
   *       console.log('Lugar seleccionado:', placeData);
   *     }
   *   );
   * }
   * 
   * ngOnDestroy() {
   *   if (this.autocompleteInstance) {
   *     this.locationService.destroyAutocomplete(this.autocompleteInstance);
   *   }
   * }
   * ```
   * 
   * HTML:
   * ```html
   * <div #addressContainer></div>
   * ```
   * 
   * @param containerElement - El elemento contenedor donde se insertará el autocomplete
   * @param onPlaceSelected - Callback que se ejecuta cuando se selecciona un lugar
   * @returns El objeto PlaceAutocompleteElement de Google
   */
  initializeGoogleAutocomplete(
    containerElement: HTMLElement,
    onPlaceSelected: (placeData: {
      address: string;
      street: string;
      city: string;
      lat: number;
      lng: number;
      formatted_address: string;
      place_name?: string;
      street_number?: string;
      route?: string;
    }) => void
  ): any {
    // 1. DEFINIR LOS LÍMITES GEOGRÁFICOS
    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(39.21336405907106, 2.286124031276248),
      new google.maps.LatLng(39.996529359153115, 3.529999646106621)
    );

    // 2. CREAR EL NUEVO PlaceAutocompleteElement
    try {
      const placeAutocomplete: any = new (google.maps.places as any).PlaceAutocompleteElement({
        locationRestriction: bounds,
        componentRestrictions: { country: 'es' }
      });

      // Configurar atributos adicionales
      placeAutocomplete.setAttribute('placeholder', 'Escribe una dirección...');

      // Agregar el elemento al contenedor
      containerElement.innerHTML = ''; // Limpiar contenedor
      containerElement.appendChild(placeAutocomplete);

      // 3. GESTIONAR LA SELECCIÓN DEL USUARIO
      placeAutocomplete.addEventListener('gmp-select', async (event: any) => {
        const prediction = event.placePrediction;

        if (!prediction) {
          console.warn("No se encontró predicción en el evento.");
          return;
        }

        try {
          // Usar PlacesService para obtener los detalles completos
          const service = new google.maps.places.PlacesService(document.createElement('div'));

          // Solicitar detalles del lugar usando el place_id
          service.getDetails(
            {
              placeId: prediction.placeId,
              fields: ['address_components', 'geometry', 'name', 'formatted_address']
            },
            (place: any, status: any) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
                console.error('Error al obtener detalles del lugar:', status);
                return;
              }

              // Extraer la información necesaria
              const formatted_address = place.formatted_address || '';
              const place_name = place.name || '';

              // Buscar componentes de dirección
              const addressComponents = place.address_components || [];
              const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name || '';
              const route = addressComponents.find((c: any) => c.types.includes('route'))?.long_name || '';
              const street_number = addressComponents.find((c: any) => c.types.includes('street_number'))?.long_name || '';

              // Obtener coordenadas
              const lat = place.geometry?.location?.lat() || 0;
              const lng = place.geometry?.location?.lng() || 0;

              // Determinar el street
              let street = '';
              if (route && street_number) {
                street = `${route}, ${street_number}`;
              } else if (route) {
                street = route;
              } else {
                street = place_name || formatted_address || '';
              }

              // Ejecutar el callback con los datos del lugar
              onPlaceSelected({
                address: formatted_address || '',
                street: street,
                city: city || '',
                lat: lat,
                lng: lng,
                formatted_address: formatted_address || '',
                place_name: place_name,
                route: route,
                street_number: street_number
              });
            }
          );
        } catch (error) {
          console.error('Error al procesar el lugar:', error);
        }
      });

      return placeAutocomplete;
    } catch (error) {
      console.error('Error al crear PlaceAutocompleteElement:', error);
      throw error;
    }
  }

  /**
   * Método SÚPER SIMPLE para configurar Google Autocomplete (NUEVO - PlaceAutocompleteElement)
   * 
   * EJEMPLO DE USO (MÍNIMO CÓDIGO EN COMPONENTE):
   * ```typescript
   * ngAfterViewInit() {
   *   this.autocompleteInstance = this.locationService.setupAutocomplete(
   *     this.containerElement.nativeElement,
   *     (data) => this.handlePlaceSelected(data)
   *   );
   * }
   * 
   * handlePlaceSelected(data) {
   *   this.address = data.formatted_address;
   *   this.coordinates = { lat: data.lat, lng: data.lng };
   *   // Tu lógica específica aquí...
   * }
   * ```
   * 
   * HTML:
   * ```html
   * <div #containerElement></div>
   * ```
   * 
   * @param containerElement - El elemento contenedor donde se insertará el autocomplete
   * @param onPlaceSelected - Callback simple que recibe los datos del lugar
   * @returns El objeto PlaceAutocompleteElement para cleanup
   */
  setupAutocomplete(
    containerElement: HTMLElement,
    onPlaceSelected: (data: {
      formatted_address: string;
      street: string;
      city: string;
      lat: number;
      lng: number;
      place_name?: string;
      route?: string;
      street_number?: string;
    }) => void
  ): any {
    // Usar el método existente pero con interfaz simplificada
    return this.initializeGoogleAutocomplete(
      containerElement,
      (placeData) => {
        // Convertir a formato más simple y directo
        onPlaceSelected({
          formatted_address: placeData.formatted_address,
          street: placeData.street,
          city: placeData.city,
          lat: placeData.lat,
          lng: placeData.lng,
          place_name: placeData.place_name,
          route: placeData.route,
          street_number: placeData.street_number
        });
      }
    );
  }

  /**
   * Método para limpiar los event listeners y destruir PlaceAutocompleteElement
   * @param placeAutocompleteElement - El PlaceAutocompleteElement a destruir
   */
  destroyAutocomplete(placeAutocompleteElement: any): void {
    if (placeAutocompleteElement && placeAutocompleteElement.parentNode) {
      // Remover todos los event listeners
      const clone = placeAutocompleteElement.cloneNode(false);
      placeAutocompleteElement.parentNode.replaceChild(clone, placeAutocompleteElement);

      // Eliminar el elemento del DOM
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
    }
  }

}
